import { type RPPDomain } from '@retailer-platform/dashboard/core'
import {
  NavItem,
  NavEntryPoint,
  AdminNavEntryPoint,
  AdminNavEntryPointV2,
} from '@retailer-platform/dashboard/routing'
import { insightsPortalRouter } from './routing/insightsPortalRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import ES_ES from './translations/es-ES.json'
import FR_FR from './translations/fr-FR.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { insightsPortalAccessControl } from './access-control/insightsPortalAccess.configuration'
import { insightsPortalSummaryReportAccessControl } from './access-control/insightsPortalSummaryReportAccess.configuration'
import { insightsPortalAdsDashboardAccessControl } from './access-control/insightsPortalAdsDashboardAccess.configuration'
import { insightsPortalCampaignInsightsAccessControl } from './access-control/insightsPortalCampaignInsights.configuration'
import { insightsPortalDataExplorerAccessControl } from './access-control/insightsPortalDataExplorerAccess.configuration'
import { insightsPortalCollectionsAccessControl } from './access-control/insightsPortalCollectionInsights.configuration'
import { insightsPortalCreativeInsightsAccessControl } from './access-control/insightsPortalCreativeInsights.configuration'
import { insightsPortalDashboardBuilderControl } from './access-control/insightsPortalDashboardBuilderAccess.configuration'
import { InsightsPortalStoreLaborPerformanceAccessControl } from './access-control/insightsPortalStoreLaborPerformanceAccess.configuration'
import { insightsPortalAwarenessCampaignInsightsAccessControl } from './access-control/insightsPortalAwarenessCampaignInsights.configuration'
import { insightsPortalFlyersAccessControl } from './access-control/insightsPortalFlyersAccess.configuration'
import { InsightsPortalWarehouseContextFilterNavWrapper } from './routing/InsightsPortalWarehouseContextFilterNavWrapper'
import { insightsAdminRouter } from './routing/insightsAdminRouter'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'insights-portal',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'insights-portal-dashboard-tool',
  },
  routes,
  translations: { EN_US, ES_ES, FR_FR },
  routers: {
    scoped: [insightsPortalRouter],
    admin: [insightsAdminRouter],
  },
  navigationEntries: [
    {
      attachTo: NavItem.DataAndAnalytics,
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View a summary dashboard of top sales, order fulfillment, and customer engagement metrics across your stores',
          labelId: 'insightsPortalDomain.summaryReport.nav',
          route: 'insights-portal-executive-summary',
          accessControl: insightsPortalSummaryReportAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 0,
          positionNavV2: 0,
          inToolPermissions: [
            {
              slug: Permission.InsightsPortalCampaign,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.InsightsPortalCampaign,
            },
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View store order fulfillment, processing time, and completion rate analytics',
          labelId: 'insightsPortalDomain.storePerformance.completedOrders.title',
          route: 'insights-portal-store-performance-completed-orders',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 1,
          positionNavV2: 1,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View item-level customer shopping behavior and basket composition analytics',
          labelId: 'insightsPortalDomain.storePerformance.basket.title',
          route: 'insights-portal-store-performance-basket-overview',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 2,
          positionNavV2: 2,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View search analytics and customer search behavior patterns',
          labelId: 'insightsPortalDomain.storePerformance.search.title',
          route: 'insights-portal-store-performance-search',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 3,
          positionNavV2: 3,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View product image coverage and completeness metrics',
          labelId: 'insightsPortalDomain.searchDashboard.metrics.imageCoverage',
          route: 'insights-portal-store-performance-image-coverage',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 4,
          positionNavV2: 4,
        },
      ],
    },

    {
      attachToV2: NavEntryPoint.OperationsLabor,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View labor productivity, staffing levels, and workforce scheduling performance across stores',
          labelId: 'insightsPortalDomain.storeLaborPerformanceV2.title',
          route: 'insights-portal-store-labor-performance-v2',
          accessControl: InsightsPortalStoreLaborPerformanceAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 1,
          positionNavV2: 1,
          inToolPermissions: [
            {
              slug: Permission.Analytics,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.Analytics,
            },
            {
              slug: Permission.PickupAnalytics,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.PickupAnalytics,
            },
          ],
        },
      ],
    },
    {
      attachTo: NavItem.DataAndAnalytics,
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View advertising performance metrics across sponsored products, display ads, and promotions',
          labelId: 'insightsPortalDomain.adsDashboard.nav',
          route: 'insights-portal-overview-ads-dashboard',
          accessControl: insightsPortalAdsDashboardAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 5,
          positionNavV2: 3,
          subRoutes: [
            'insights-portal-sponsored-product-ads-dashboard',
            'insights-portal-display-ads-dashboard',
            'insights-portal-promotions-ads-dashboard',
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View marketing campaign performance and ROI analytics',
          labelId: 'insightsPortalDomain.campaignsAnalytics.nav',
          route: 'insights-portal-marketing-campaigns-analytics-overview',
          accessControl: insightsPortalCampaignInsightsAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 7,
          positionNavV2: 0,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMerchandising,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View collection performance and merchandising analytics',
          labelId: 'insightsPortalDomain.collections.nav',
          route: 'insights-portal-merchandising-collections-overview',
          accessControl: insightsPortalCollectionsAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 9,
          inToolPermissions: [],
        },
      ],
    },
    {
      attachTo: NavItem.DataAndAnalytics,
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View creative placements and asset performance analytics',
          labelId: 'insightsPortalDomain.creatives.nav',
          route: 'insights-portal-placements-insights',
          accessControl: insightsPortalCreativeInsightsAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 10,
          positionNavV2: 1,
        },
      ],
    },
    {
      attachTo: NavItem.DataAndAnalytics,
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View awareness campaign performance and brand metrics',
          labelId: 'insightsPortalDomain.awarenessCampaigns.nav',
          route: 'insights-portal-marketing-awareness-campaigns-analytics-overview',
          accessControl: insightsPortalAwarenessCampaignInsightsAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          position: 8,
          positionNavV2: 2,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsReportsAndLogs,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create custom reports, explore detailed analytics data, and export analysis results',
          labelId: 'insightsPortalDomain.dataExplorer.navBeta',
          route: 'insights-portal-reports-logs-custom-report-list',
          accessControl: insightsPortalDataExplorerAccessControl,
          positionNavV2: 1,
          subRoutes: ['insights-portal-reports-logs-custom-report-exports-list'],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View flyer performance',
          labelId: 'insightsPortalDomain.flyers.nav',
          route: 'insights-portal-marketing-flyers-overview',
          accessControl: insightsPortalFlyersAccessControl,
          NavItemWrapper: InsightsPortalWarehouseContextFilterNavWrapper,
          positionNavV2: 4,
          subRoutes: ['insights-portal-marketing-flyers-single-flyer'],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachTo: AdminNavEntryPoint.Analytics,
      attachToV2: AdminNavEntryPointV2.Analytics,
      subNavItems: [
        {
          route: 'insights-portal-admin-dashboard-create',
          labelId: 'insightsPortalDomain.page.dashboardBuilder.nav',
          accessControl: insightsPortalDashboardBuilderControl,
        },
      ],
    },
  ],
}

export default definition
